import React, { useState } from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { FaWhatsapp } from "react-icons/fa";
import SEO from "../components/SEO";

const C = {
  bg: "#f8f9fd", dark: "#1a1a2e", gold: "#f5c518", goldD: "#d4a80f",
  gray: "#6b7280", grayL: "#9ca3af", border: "#e5e7eb", white: "#ffffff",
};
const F = { display: "'Playfair Display', serif", body: "'Plus Jakarta Sans', sans-serif" };

const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700;800;900&family=Plus+Jakarta+Sans:wght@300;400;500;600;700;800&display=swap');

  @keyframes fadeUp { from{opacity:0;transform:translateY(28px)} to{opacity:1;transform:translateY(0)} }
  @keyframes pulse  { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:.5;transform:scale(1.4)} }
  @keyframes float  { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-8px)} }

  *, *::before, *::after { box-sizing:border-box; margin:0; padding:0; }
  body { font-family:'Plus Jakarta Sans',sans-serif; overflow-x:hidden; }

  .step-card {
    background:#fff;
    border-radius:24px;
    border:1.5px solid #e5e7eb;
    padding:32px 28px;
    box-shadow:0 6px 24px rgba(0,0,0,.06);
    transition:all .3s;
    position:relative;
    overflow:hidden;
  }
  .step-card::before {
    content:'';
    position:absolute;
    top:0; left:0; right:0;
    height:4px;
    background:linear-gradient(90deg,#f5c518,#d4a80f);
    transform:scaleX(0);
    transform-origin:left;
    transition:transform .3s ease;
  }
  .step-card:hover::before { transform:scaleX(1); }
  .step-card:hover {
    transform:translateY(-8px);
    border-color:#f5c518;
    box-shadow:0 20px 50px rgba(245,197,24,.14);
  }

  .faq-item {
    background:#fff;
    border-radius:14px;
    border:1.5px solid #e5e7eb;
    overflow:hidden;
    transition:border-color .25s, box-shadow .25s;
    cursor:pointer;
  }
  .faq-item:hover { border-color:#f5c518; }
  .faq-item.open  { border-color:#f5c518; box-shadow:0 4px 20px rgba(245,197,24,.12); }

  .tip-card {
    border-radius:18px;
    padding:20px 18px;
    border:1px solid;
    transition:transform .25s;
  }
  .tip-card:hover { transform:translateY(-4px); }

  .cta-book:hover {
    transform:translateY(-2px);
    box-shadow:0 12px 36px rgba(245,197,24,.45) !important;
  }

  @media(max-width:768px) {
    .hero-sec  { padding:52px 16px 40px !important; }
    .sec-pad   { padding:52px 16px !important; }
    .steps-grid{ grid-template-columns:1fr !important; }
    .hero-h1   { font-size:clamp(32px,8vw,52px) !important; }
    .connector { display:none !important; }
  }
`;

const STEPS = [
  {
    num: "01",
    icon: "📍",
    title: "Enter Your Trip Details",
    desc: "Tell us where you're going. Enter your pickup location, destination, travel date, and preferred vehicle type on our home page.",
    tips: ["Use Google autocomplete for accurate addresses", "Select One-Way or Round Trip", "Choose from Sedan, SUV, Innova, or Hatchback"],
  },
  {
    num: "02",
    icon: "🔍",
    title: "Browse Available Cabs",
    desc: "We instantly show you all available, verified cabs near you — with full pricing, driver profiles, ratings, and estimated arrival times.",
    tips: ["Filter by vehicle type", "Sort by price or rating", "See real driver reviews & trip count"],
  },
  {
    num: "03",
    icon: "✅",
    title: "Confirm Your Booking",
    desc: "Pick your cab, enter your name, mobile, and confirm. Your booking is instantly locked in — no payment needed upfront.",
    tips: ["Booking confirmed via SMS & call", "HaloSorath contacts you directly", "Zero middleman — you deal directly"],
  },
  {
    num: "04",
    icon: "🚗",
    title: "Enjoy Your Ride",
    desc: "Your driver arrives at your location, you travel comfortably, and pay directly at the end of the trip. Simple, safe, and transparent.",
    tips: ["Pay cash, UPI, or card directly to HaloSorath", "Toll & fuel already included in fare", "Rate your driver after the ride"],
  },
];

const FAQS = [
  { q: "How do I book a cab on HaloSorath?",                 a: "Simply enter your pickup, destination, date, and vehicle type on our home page. Click 'Find Cabs Now', choose a cab, fill in your details, and confirm." },
  { q: "Do I need to pay anything upfront?",               a: "No! HaloSorath requires zero advance payment. You confirm your booking, the halosorath calls you, and you pay directly after your trip — cash, UPI, or card." },
  { q: "How are drivers verified?",                        a: "All drivers undergo police background checks, document verification, and vehicle inspection before being listed. Customer ratings keep quality high." },
  { q: "What is included in the fare?",                    a: "The quoted fare includes vehicle charges, fuel, driver allowance, toll charges, and state taxes. Only 5% GST and parking (if applicable) are extra." },
  
  { q: "Is GPS tracking available?",                       a: "Yes. You can track your cab live on the map once your booking is confirmed. Share your live trip location with family for safety." },
  { q: "Do you cover outstation trips?",                   a: "Absolutely. We specialize in outstation trips across Gujarat and beyond. One-way, round trip, and multi-city routes are all supported." },
  { q: "What vehicles are available?",                     a: "We offer Sedan (Dzire/Etios), SUV (Ertiga/Carens), Innova/Innova Crysta, Hatchback (WagonR/Swift), and Tempo Traveller for group trips." },
];

const TIPS = [
  { icon: "📅", bg: "rgba(245,197,24,.08)", border: "rgba(245,197,24,.25)", title: "Book in Advance", desc: "For airport transfers and outstation trips, book at least 2–3 hours ahead for best availability." },
  { icon: "🗺️", bg: "rgba(16,185,129,.08)", border: "rgba(16,185,129,.25)", title: "Share Your Location", desc: "Use Google Maps pin for pickup to help your driver find you quickly, especially in busy areas." },
  { icon: "💬", bg: "rgba(59,130,246,.08)", border: "rgba(59,130,246,.25)", title: "WhatsApp for Quick Help", desc: "WhatsApp us your booking ID for instant support — faster than a phone call." },
  { icon: "⭐", bg: "rgba(139,92,246,.08)", border: "rgba(139,92,246,.25)", title: "Rate Your Driver", desc: "Your feedback directly helps other riders and rewards our best drivers." },
];

export default function HowItWorksPage() {
  const [openFaq, setOpenFaq] = useState(null);

  return (
    <>
    <SEO page="howItWorks" />
      <style>{CSS}</style>
      <div style={{ fontFamily: F.body, background: C.bg, color: C.dark, overflowX: "hidden" }}>
        <Header defaultActive="How It Works" />

        {/* ── HERO ── */}
        <div className="hero-sec" style={{ background: "linear-gradient(150deg,#f8f9fd 0%,#fffbea 55%,#f8f9fd 100%)", padding: "72px 24px 56px", textAlign: "center", position: "relative", overflow: "hidden" }}>
          <div style={{ position: "absolute", top: -60, right: -60, width: 280, height: 280, borderRadius: "50%", background: "rgba(245,197,24,.07)", pointerEvents: "none" }} />
          <div style={{ position: "absolute", bottom: -40, left: -40, width: 200, height: 200, borderRadius: "50%", background: "rgba(26,26,46,.04)", pointerEvents: "none" }} />

          <div style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "rgba(245,197,24,.1)", border: "1px solid rgba(245,197,24,.3)", borderRadius: 50, padding: "6px 16px", fontSize: 11, color: "#b8860b", fontWeight: 700, letterSpacing: "1px", textTransform: "uppercase", marginBottom: 20 }}>
            <span style={{ width: 6, height: 6, background: C.gold, borderRadius: "50%", animation: "pulse 1.8s ease-in-out infinite" }} />
            Simple Process
          </div>

          <h1 className="hero-h1" style={{ fontFamily: F.display, fontSize: "clamp(36px,6vw,68px)", fontWeight: 900, color: C.dark, letterSpacing: "-2px", lineHeight: 1.1, marginBottom: 16, animation: "fadeUp .7s ease both" }}>
            Booking a Cab Has<br /><span style={{ color: C.gold }}>Never Been Simpler</span>
          </h1>
          <p style={{ fontSize: "clamp(14px,1.8vw,17px)", color: C.gray, maxWidth: 500, margin: "0 auto 32px", lineHeight: 1.7, animation: "fadeUp .8s .1s ease both" }}>
            4 easy steps, zero confusion. From search to your destination — HaloSorath gets you there smoothly.
          </p>
          
        </div>

        {/* ── STEPS ── */}
        <section className="sec-pad" style={{ padding: "80px 24px" }}>
          <div style={{ maxWidth: 1100, margin: "0 auto" }}>
            <div style={{ textAlign: "center", marginBottom: 56 }}>
              <p style={{ fontSize: 11, letterSpacing: "2.5px", textTransform: "uppercase", fontWeight: 700, color: C.goldD, marginBottom: 10 }}>Step by Step</p>
              <h2 style={{ fontFamily: F.display, fontSize: "clamp(26px,4vw,44px)", fontWeight: 800, color: C.dark, lineHeight: 1.15 }}>How It Works</h2>
            </div>

            <div className="steps-grid" style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 20 }}>
              {STEPS.map((s, i) => (
                <div key={i} style={{ display: "flex", flexDirection: "column", alignItems: "center", position: "relative" }}>
                  {/* Connector line */}
                  {i < STEPS.length - 1 && (
                    <div className="connector" style={{ position: "absolute", top: 28, left: "calc(50% + 36px)", width: "calc(100% - 12px)", height: 2, background: "linear-gradient(90deg,#f5c518,rgba(245,197,24,.15))", zIndex: 0 }} />
                  )}

                  <div className="step-card" style={{ width: "100%", zIndex: 1 }}>
                    {/* Step number badge */}
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
                      <div style={{ width: 52, height: 52, borderRadius: "50%", background: `linear-gradient(135deg,${C.gold},${C.goldD})`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24, boxShadow: "0 6px 20px rgba(245,197,24,.3)" }}>
                        {s.icon}
                      </div>
                      <div style={{ fontFamily: F.display, fontSize: 42, fontWeight: 900, color: "rgba(245,197,24,.15)", lineHeight: 1 }}>{s.num}</div>
                    </div>

                    <h3 style={{ fontFamily: F.display, fontSize: "clamp(15px,1.8vw,18px)", fontWeight: 800, color: C.dark, marginBottom: 10, lineHeight: 1.3 }}>{s.title}</h3>
                    <p style={{ fontSize: 13, color: C.grayL, lineHeight: 1.7, marginBottom: 18 }}>{s.desc}</p>

                    <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
                      {s.tips.map((t, j) => (
                        <div key={j} style={{ display: "flex", alignItems: "flex-start", gap: 7, fontSize: 12, color: "#374151" }}>
                          <span style={{ color: C.goldD, fontWeight: 700, flexShrink: 0, marginTop: 1 }}>→</span>
                          {t}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── PRO TIPS ── */}
        <section className="sec-pad" style={{ background: C.dark, padding: "72px 24px" }}>
          <div style={{ maxWidth: 1000, margin: "0 auto" }}>
            <div style={{ textAlign: "center", marginBottom: 44 }}>
              <p style={{ fontSize: 11, letterSpacing: "2.5px", textTransform: "uppercase", fontWeight: 700, color: C.goldD, marginBottom: 10 }}>Insider Tips</p>
              <h2 style={{ fontFamily: F.display, fontSize: "clamp(24px,4vw,40px)", fontWeight: 800, color: C.white, lineHeight: 1.15 }}>Get the Most From Your Ride</h2>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))", gap: 16 }}>
              {TIPS.map((t, i) => (
                <div key={i} className="tip-card" style={{ background: t.bg, borderColor: t.border }}>
                  <div style={{ fontSize: 32, marginBottom: 12 }}>{t.icon}</div>
                  <h4 style={{ fontFamily: F.display, fontSize: 16, fontWeight: 800, color: C.white, marginBottom: 8 }}>{t.title}</h4>
                  <p style={{ fontSize: 13, color: "rgba(255,255,255,.6)", lineHeight: 1.7 }}>{t.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── FAQ ── */}
        <section className="sec-pad" style={{ background: "#f0f2f8", padding: "72px 24px" }}>
          <div style={{ maxWidth: 720, margin: "0 auto" }}>
            <div style={{ textAlign: "center", marginBottom: 44 }}>
              <p style={{ fontSize: 11, letterSpacing: "2.5px", textTransform: "uppercase", fontWeight: 700, color: C.goldD, marginBottom: 10 }}>FAQ</p>
              <h2 style={{ fontFamily: F.display, fontSize: "clamp(24px,4vw,40px)", fontWeight: 800, color: C.dark }}>Got Questions?</h2>
              <p style={{ fontSize: "clamp(13px,1.7vw,15px)", color: C.grayL, marginTop: 10 }}>Everything you need to know before your first HaloSorath ride.</p>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {FAQS.map((f, i) => (
                <div key={i} className={`faq-item${openFaq === i ? " open" : ""}`} onClick={() => setOpenFaq(openFaq === i ? null : i)}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "17px 20px", gap: 12 }}>
                    <span style={{ fontSize: "clamp(13px,1.6vw,15px)", fontWeight: 700, color: C.dark, lineHeight: 1.4 }}>{f.q}</span>
                    <span style={{ color: C.gold, fontSize: 22, fontWeight: 300, transition: "transform .25s", display: "inline-block", transform: openFaq === i ? "rotate(45deg)" : "none", flexShrink: 0 }}>+</span>
                  </div>
                  {openFaq === i && (
                    <div style={{ padding: "0 20px 18px", paddingTop: 2, fontSize: 13.5, color: C.gray, lineHeight: 1.75, borderTop: `1px solid ${C.border}` }}>
                      {f.a}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── CTA ── */}
        {/* <section style={{ background: `linear-gradient(135deg,${C.gold},${C.goldD})`, padding: "60px 24px", textAlign: "center" }}>
          <h2 style={{ fontFamily: F.display, fontSize: "clamp(24px,4.5vw,46px)", fontWeight: 900, color: C.dark, marginBottom: 12 }}>Ready to Ride?</h2>
          <p style={{ fontSize: "clamp(13px,1.8vw,17px)", color: "rgba(26,26,46,.65)", maxWidth: 420, margin: "0 auto 28px" }}>Book in 60 seconds. No app. No advance payment. No stress.</p>
          <div style={{ display: "flex", gap: 14, justifyContent: "center", flexWrap: "wrap" }}>
            <a href="/" style={{ textDecoration: "none" }}>
              <button className="cta-book" style={{ background: C.dark, color: C.gold, border: "none", borderRadius: 50, padding: "14px 40px", fontSize: 15, fontWeight: 800, fontFamily: F.display, cursor: "pointer", transition: "all .25s" }}>🚗 Book Now</button>
            </a>
            <a href="tel:+919265407669" style={{ textDecoration: "none" }}>
              <button style={{ background: "transparent", color: C.dark, border: `2px solid ${C.dark}`, borderRadius: 50, padding: "14px 40px", fontSize: 15, fontWeight: 700, fontFamily: F.body, cursor: "pointer" }}>📞 Call Us</button>
            </a>
          </div>
        </section> */}

        <Footer />

        
      </div>
    </>
  );
}